import React from "react";
import { Box, Typography } from "@mui/material";
import RoomServiceRoundedIcon from "@mui/icons-material/RoomServiceRounded";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import {
  CATERING_BORDER,
  CATERING_COLOR,
  CATERING_SOFT,
  cateringDeliveryLine,
  cateringMinimumLabel,
  cateringNotice,
} from "../../utils/catering";
import { FONT_DISPLAY } from "../../utils/designTokens";
import RmfDialog, { DialogActions, DialogIcon, DialogText, DialogTitle, dialogButtonSx } from "../Dialog/RmfDialog";

// Shared catering UI (backend migration 131): the cloche badge, the band at the
// top of a restaurant's Catering view, the checkout header and the dialog that
// keeps catering and regular items in separate carts.

const SERIF = FONT_DISPLAY;

/** Small cloche pill: "Catering", "Catering deal". */
export function CateringBadge({ label = "Catering", size = "small", sx }) {
  const small = size === "small";
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        height: small ? 20 : 24,
        px: small ? 0.75 : 1,
        borderRadius: 999,
        bgcolor: CATERING_SOFT,
        color: CATERING_COLOR,
        border: `1px solid ${CATERING_BORDER}`,
        fontSize: small ? "0.65rem" : "0.72rem",
        fontWeight: 700,
        lineHeight: 1,
        whiteSpace: "nowrap",
        flexShrink: 0,
        ...sx,
      }}
    >
      <RoomServiceRoundedIcon sx={{ fontSize: small ? 12 : 14 }} />
      {label}
    </Box>
  );
}

// A quiet white pill for the facts under the Catering heading.
function Fact({ children }) {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        height: 26,
        px: 1.25,
        borderRadius: 999,
        bgcolor: "#fff",
        border: `1px solid ${CATERING_BORDER}`,
        color: CATERING_COLOR,
        fontSize: "0.75rem",
        fontWeight: 700,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </Box>
  );
}

/** The band at the top of a restaurant's Catering view. */
export function CateringMenuHeader({ settings, currencySymbol = "$" }) {
  const notice = cateringNotice(settings?.leadTimeMinutes);
  const delivery = cateringDeliveryLine(settings);
  const minimum = cateringMinimumLabel(settings?.minimumOrder, currencySymbol);
  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        display: "flex",
        alignItems: "flex-start",
        gap: { xs: 1.5, md: 2 },
        mb: 3,
        p: { xs: 2, md: 2.5 },
        borderRadius: "18px",
        bgcolor: CATERING_SOFT,
        border: `1px solid ${CATERING_BORDER}`,
      }}
    >
      {/* Oversized cloche watermark — decoration only. */}
      <RoomServiceRoundedIcon
        aria-hidden
        sx={{
          position: "absolute",
          right: { xs: -18, md: -10 },
          bottom: { xs: -30, md: -36 },
          fontSize: { xs: 120, md: 150 },
          color: CATERING_COLOR,
          opacity: 0.07,
          pointerEvents: "none",
        }}
      />
      <Box
        sx={{
          flexShrink: 0,
          width: { xs: 44, md: 52 },
          height: { xs: 44, md: 52 },
          borderRadius: "50%",
          bgcolor: CATERING_COLOR,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 6px 16px rgba(204,0,0,0.24)",
        }}
      >
        <RoomServiceRoundedIcon sx={{ color: "#fff", fontSize: { xs: 24, md: 28 } }} />
      </Box>
      <Box sx={{ position: "relative", minWidth: 0, flex: 1 }}>
        <Typography
          component="h2"
          sx={{
            fontFamily: SERIF,
            fontWeight: 700,
            fontSize: { xs: "1.45rem", md: "1.7rem" },
            lineHeight: 1.1,
            letterSpacing: "-0.01em",
            color: CATERING_COLOR,
          }}
        >
          Catering
        </Typography>
        {notice && (
          <Typography sx={{ mt: 0.5, fontSize: "0.92rem", fontWeight: 600, color: "#1e293b" }}>
            {notice}
          </Typography>
        )}
        {(delivery || minimum) && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1.25 }}>
            {delivery && <Fact>{delivery}</Fact>}
            {minimum && <Fact>{minimum}</Fact>}
          </Box>
        )}
      </Box>
    </Box>
  );
}

/** "Catering order" header at the top of checkout for a catering cart. */
export function CateringOrderBanner({ settings, currencySymbol = "$" }) {
  const notice = cateringNotice(settings?.leadTimeMinutes);
  const minimum = cateringMinimumLabel(settings?.minimumOrder, currencySymbol);
  const sub = [notice, minimum].filter(Boolean).join(" · ");
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        mb: 2,
        px: { xs: 1.5, md: 2 },
        py: 1.5,
        borderRadius: "16px",
        bgcolor: CATERING_SOFT,
        border: `1px solid ${CATERING_BORDER}`,
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          width: 38,
          height: 38,
          borderRadius: "50%",
          bgcolor: CATERING_COLOR,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <RoomServiceRoundedIcon sx={{ color: "#fff", fontSize: 21 }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 800, fontSize: "0.98rem", color: "#0f172a", lineHeight: 1.25 }}>
          Catering order
        </Typography>
        {sub && (
          <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: CATERING_COLOR, lineHeight: 1.35 }}>
            {sub}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

/**
 * Catering is its own cart. Shown when an item of the other kind is added:
 * confirming starts a new cart with that item, "Keep my cart" changes nothing.
 */
export function CateringCartDialog({ open, toCatering, onConfirm, onCancel }) {
  const title = toCatering ? "Start a catering order?" : "Start a regular order?";
  const body = toCatering
    ? "Catering is ordered separately from regular items. Starting a catering order will clear your current cart."
    : "Your cart has catering items, which are ordered separately. Starting a regular order will clear your current cart.";
  return (
    <RmfDialog open={open} onClose={onCancel}>
      <DialogIcon tone={toCatering ? "red" : "neutral"} sx={{ mb: 2.5 }}>
        {toCatering ? <RoomServiceRoundedIcon /> : <ShoppingBagOutlinedIcon />}
      </DialogIcon>
      <DialogTitle besideClose={false}>{title}</DialogTitle>
      <DialogText>{body}</DialogText>
      <DialogActions>
        <Box component="button" type="button" sx={dialogButtonSx.primary} onClick={onConfirm}>
          {toCatering ? "Start catering order" : "Start regular order"}
        </Box>
        <Box component="button" type="button" sx={dialogButtonSx.secondary} onClick={onCancel}>
          Keep my cart
        </Box>
      </DialogActions>
    </RmfDialog>
  );
}
